import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Collapse ,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Science as ScienceIcon,
  Timeline as TimelineIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import axios from 'axios';
import { loadHaematologyParameters, addLabCategory, loadLabCategories,
   deleteLabCategory, updateLabCategory, addLabParameters, updateLabParameter, 
   deleteLabParameter } from '../BaseURL';
import Swal from 'sweetalert2';
import { AccessTime } from '@mui/icons-material';

const Home = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [haematologyData, setHaematologyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [formData, setFormData] = useState([{
    id: Date.now(),
    parameterName: '',
    type: '',
    maleRange: '',
    femaleRange: '',
    childrenRange: '',
    neonateRange: '',
    unit: '',
    flags: ''
  }]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryFormData, setCategoryFormData] = useState({
    categoryName: '',
    waitingTime: ''
  });
  const [categories, setCategories] = useState([]);
  const [editingParameter, setEditingParameter] = useState(null);
  const [editParameterFormData, setEditParameterFormData] = useState({
    parameterName: '',
    type: '',
    maleRange: '',
    femaleRange: '',
    childrenRange: '',
    neonateRange: '',
    unit: '',
    flags: ''
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryFormData, setEditCategoryFormData] = useState({
  categoryName: '',
  waitingTime: ''
});

  // Pagination and filtering states
  const [categoryPage, setCategoryPage] = useState(0);
  const [categoryRowsPerPage, setCategoryRowsPerPage] = useState(10);
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [labSearchTerm, setLabSearchTerm] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');

  // Fetch haematology parameters on component mount
  useEffect(() => {
    fetchHaematologyParameters();
    fetchCategories();
  }, []);

  const fetchHaematologyParameters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(loadHaematologyParameters);
      if (response.data.success) {
        setHaematologyData(response.data.results);
      }
    } catch (error) {
      console.error('Error fetching haematology parameters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeCategoryPage = (event, newPage) => {
    setCategoryPage(newPage);
  };

  const handleChangeCategoryRowsPerPage = (event) => {
    setCategoryRowsPerPage(parseInt(event.target.value, 10));
    setCategoryPage(0);
  };

  const toggleRowExpansion = (groupIndex) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(groupIndex)) {
      newExpandedRows.delete(groupIndex);
    } else {
      newExpandedRows.add(groupIndex);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    // Reset form
    setFormData([{
      id: Date.now(),
      parameterName: '',
      type: '',
      maleRange: '',
      femaleRange: '',
      childrenRange: '',
      neonateRange: '',
      unit: '',
      flags: ''
    }]);
    setSelectedCategory('');
  };

  const handleInputChange = (parameterId, field, value) => {
    setFormData(prev => prev.map(param =>
      param.id === parameterId
        ? { ...param, [field]: value }
        : param
    ));
  };

  const addParameterRow = () => {
    const newParameter = {
      id: Date.now(),
      parameterName: '',
      type: '',
      maleRange: '',
      femaleRange: '',
      childrenRange: '',
      neonateRange: '',
      unit: '',
      flags: ''
    };
    setFormData(prev => [newParameter, ...prev]);
  };

  const removeParameterRow = (parameterId) => {
    if (formData.length > 1) {
      setFormData(prev => prev.filter(param => param.id !== parameterId));
    }
  };

  const handleSave = async () => {
    // Validate that category is selected
    if (!selectedCategory) {
      await Swal.fire({
        title: 'Category Required',
        text: 'Please select a category before adding parameters',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Validate that at least one parameter has a name
    const validParameters = formData.filter(param => param.parameterName.trim());
    if (validParameters.length === 0) {
      await Swal.fire({
        title: 'Parameters Required',
        text: 'Please add at least one parameter with a name',
        icon: 'warning',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      });
      return;
    }

    try {
      // Show loading
      Swal.fire({
        title: 'Saving Parameters...',
        text: 'Please wait while we save your lab parameters',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      // Prepare parameters for bulk save
      const parametersToSave = validParameters.map(param => ({
        category: selectedCategory,
        parameterName: param.parameterName.trim(),
        type: param.type,
        maleRange: param.maleRange,
        femaleRange: param.femaleRange,
        childrenRange: param.childrenRange,
        neonateRange: param.neonateRange,
        unit: param.unit,
        flags: param.flags
      }));

      console.log('Saving lab parameters:', parametersToSave);

      // Call the bulk API endpoint
      const response = await axios.post(addLabParameters, {
        parameters: parametersToSave
      });

      // Close loading
      Swal.close();

      if (response.data.success) {
        // Show success message
        await Swal.fire({
          title: 'Success!',
          text: response.data.message,
          icon: 'success',
          confirmButtonColor: '#11998e',
          confirmButtonText: 'Great!',
          timer: 2000,
          timerProgressBar: true
        });

        // Reset form BUT keep dialog open
        setFormData([{
          id: Date.now(),
          parameterName: '',
          type: '',
          maleRange: '',
          femaleRange: '',
          childrenRange: '',
          neonateRange: '',
          unit: '',
          flags: ''
        }]);
        // Keep selectedCategory so user can add more parameters to the same category
        
        // Refresh the parameters list in background
        fetchHaematologyParameters();
      } else {
        // Show error message
        await Swal.fire({
          title: 'Error',
          text: response.data.message || 'Failed to save parameters',
          icon: 'error',
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error saving parameters:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error saving parameters: ' + error.message,
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleOpenCategoryDialog = () => {
    setOpenCategoryDialog(true);
  };

  const handleCloseCategoryDialog = () => {
  setOpenCategoryDialog(false);
  setCategoryFormData({
    categoryName: '',
    waitingTime: ''
  });
};

  const handleCategoryInputChange = (field, value) => {
    setCategoryFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveCategory = async () => {
    // Check if category already exists
    const existingCategory = categories.find(cat => 
      cat.cat.toLowerCase() === categoryFormData.categoryName.trim().toLowerCase()
    );

    if (categoryFormData.waitingTime && (isNaN(categoryFormData.waitingTime) || categoryFormData.waitingTime < 0)) {
    await Swal.fire({
      title: 'Invalid Waiting Time',
      text: 'Waiting time must be a positive number',
      icon: 'warning',
      confirmButtonColor: '#3085d6',
      confirmButtonText: 'OK'
    });
    return;
  }

    try {
      const response = await axios.post(addLabCategory, {
      category: categoryFormData.categoryName,
      waitingTime: categoryFormData.waitingTime || 0  // Add this
    });
      
      if (response.data.success) {
        await Swal.fire({
          title: 'Success!',
          text: 'Category saved successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Great!'
        });
        handleCloseCategoryDialog();
        fetchCategories(); // Refresh categories list
      }
    } catch (error) {
      console.error('Error saving category:', error);
      await Swal.fire({
        title: 'Error',
        text: 'Error saving category: ' + (error.response?.data?.message || error.message),
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(loadLabCategories);
      if (response.data.success) {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleEditCategory = (category) => {
    
    setEditingCategory(category);
   setEditCategoryFormData({
  categoryName: category.cat,
  waitingTime: category.waiting_time || ''
});
  };

  const handleCloseEditDialog = () => {
    setEditingCategory(null);
   setEditCategoryFormData({
  categoryName: '',
  waitingTime:  ''
});
  };

  const handleDeleteCategory = async (categoryId) => {
  
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to delete this category?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(deleteLabCategory(categoryId));
        if (response.data.success) {
          await Swal.fire({
            title: 'Deleted!',
            text: 'Category deleted successfully',
            icon: 'success',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK'
          });
          fetchCategories(); // Refresh categories list
        } else {
          await Swal.fire({
            title: 'Error',
            text: response.data.message,
            icon: 'error',
            confirmButtonColor: '#d33',
            confirmButtonText: 'OK'
          });
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        await Swal.fire({
          title: 'Error',
          text: 'Error deleting category: ' + (error.response?.data?.message || error.message),
          icon: 'error',
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const handleEditParameter = (parameter) => {
    setEditingParameter(parameter);
    setEditParameterFormData({
      parameterName: parameter.test,
      type: parameter.type || '',
      maleRange: parameter.male || '',
      femaleRange: parameter.female || '',
      childrenRange: parameter.children || '',
      neonateRange: parameter.neonates || '',
      unit: parameter.unit || '',
      flags: parameter.flag || ''
    });
  };

  const handleCloseEditParameterDialog = () => {
    setEditingParameter(null);
    setEditParameterFormData({
      parameterName: '',
      type: '',
      maleRange: '',
      femaleRange: '',
      childrenRange: '',
      neonateRange: '',
      unit: '',
      flags: ''
    });
  };

 const handleUpdateCategory = async () => {
  try {
    const response = await axios.put(updateLabCategory(editingCategory.trans_code), {
      newCategoryName: editCategoryFormData.categoryName,
      waitingTime: editCategoryFormData.waitingTime || 0 // Include waiting time
    });
    
    if (response.data.success) {
      await Swal.fire({
        title: 'Success!',
        text: 'Category updated successfully. All related lab records have been updated.',
        icon: 'success',
        confirmButtonColor: '#11998e',
        confirmButtonText: 'Great!'
      });
      handleCloseEditDialog();
      fetchCategories(); // Refresh categories list
      fetchHaematologyParameters(); // Refresh lab parameters to show updated categories
    }
  } catch (error) {
    console.error('Error updating category:', error);
    await Swal.fire({
      title: 'Error',
      text: 'Error updating category: ' + (error.response?.data?.message || error.message),
      icon: 'error',
      confirmButtonColor: '#d33',
      confirmButtonText: 'OK'
    });
  }
};

  const handleUpdateParameter = async () => {
    if (!editingParameter) return;

    try {
      // Show loading
      Swal.fire({
        title: 'Updating Parameter...',
        text: 'Please wait while we update the lab parameter',
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await axios.put(updateLabParameter(editingParameter.trans_code), {
        parameterName: editParameterFormData.parameterName.trim(),
        maleRange: editParameterFormData.maleRange || null,
        femaleRange: editParameterFormData.femaleRange || null,
        childrenRange: editParameterFormData.childrenRange || null,
        neonateRange: editParameterFormData.neonateRange || null,
        unit: editParameterFormData.unit || null,
        flags: editParameterFormData.flags || null
      });

      // Close loading and show success
      Swal.fire({
        title: 'Success!',
        text: 'Parameter updated successfully',
        icon: 'success',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'Great!'
      });

      handleCloseEditParameterDialog();
      fetchHaematologyParameters(); // Refresh the parameters list

    } catch (error) {
      console.error('Error updating parameter:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error updating parameter: ' + (error.response?.data?.message || error.message),
        icon: 'error',
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleDeleteParameter = async (parameter) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Are you sure you want to delete the parameter "${parameter.test}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        // Show loading
        Swal.fire({
          title: 'Deleting Parameter...',
          text: 'Please wait while we delete the lab parameter',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          }
        });

        const response = await axios.delete(deleteLabParameter(parameter.trans_code));

        // Close loading and show success
        Swal.fire({
          title: 'Deleted!',
          text: 'Parameter deleted successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Great!'
        });

        fetchHaematologyParameters(); // Refresh the parameters list

      } catch (error) {
        console.error('Error deleting parameter:', error);
        Swal.fire({
          title: 'Error',
          text: 'Error deleting parameter: ' + (error.response?.data?.message || error.message),
          icon: 'error',
          confirmButtonColor: '#d33',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  // Filtered data for display
  const filteredCategories = categories.filter(category =>
    category.cat.toLowerCase().includes(categorySearchTerm.toLowerCase())
  );

  const filteredLabData = haematologyData.filter(group => {
    const matchesSearch = group.hold_show.toLowerCase().includes(labSearchTerm.toLowerCase()) ||
                         group.parameters.some(param => 
                           param.test.toLowerCase().includes(labSearchTerm.toLowerCase())
                         );
    const matchesCategory = selectedCategoryFilter === 'all' || 
                           group.parameters.some(param => param.cat === selectedCategoryFilter);
    return matchesSearch && matchesCategory;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
     
      </Box>

      <div className="row">
        {/* Categories Section - Left Side */}
        <div className="col-12 col-lg-4">
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Lab Categories
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<CategoryIcon />}
                  onClick={handleOpenCategoryDialog}
                  sx={{ borderRadius: 2 }}
                >
                  Add Category
                </Button>
              </Box>

              {/* Categories Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search categories..."
                value={categorySearchTerm}
                onChange={(e) => setCategorySearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: <ScienceIcon sx={{ color: 'action.active', mr: 1 }} />,
                }}
              />

              <TableContainer component={Paper} elevation={1}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 600 }}>Category Name</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 600, width: 80 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCategories
                      .slice(categoryPage * categoryRowsPerPage, categoryPage * categoryRowsPerPage + categoryRowsPerPage)
                      .map((category) => (
                        <TableRow key={category.id} hover>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {category.cat}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEditCategory(category)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteCategory(category.trans_code)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    {filteredCategories.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} sx={{ textAlign: 'center', py: 3 }}>
                          {categorySearchTerm ? 'No categories match your search' : 'No categories found'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredCategories.length}
                  rowsPerPage={categoryRowsPerPage}
                  page={categoryPage}
                  onPageChange={handleChangeCategoryPage}
                  onRowsPerPageChange={handleChangeCategoryRowsPerPage}
                />
              </TableContainer>
            </CardContent>
          </Card>
        </div>

        {/* Lab Parameters Section - Right Side */}
        <div className="col-12 col-lg-8">
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Lab Parameters
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenDialog}
                  sx={{ borderRadius: 2 }}
                >
                  Add Parameter
                </Button>
              </Box>

              {/* Lab Parameters Filters */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search parameters..."
                  value={labSearchTerm}
                  onChange={(e) => setLabSearchTerm(e.target.value)}
                  sx={{ flex: 1 }}
                  InputProps={{
                    startAdornment: <ScienceIcon sx={{ color: 'action.active', mr: 1 }} />,
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={selectedCategoryFilter}
                    label="Filter by Category"
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.cat}>
                        {category.cat}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} elevation={1}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                        <TableCell sx={{ color: 'white', fontWeight: 600, width: 50 }}></TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Test Group</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Parameters Count</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLabData
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((group, index) => {
                          const actualIndex = page * rowsPerPage + index;
                          const isExpanded = expandedRows.has(actualIndex);
                          return (
                            <React.Fragment key={actualIndex}>
                              <TableRow hover>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => toggleRowExpansion(actualIndex)}
                                  >
                                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                  </IconButton>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                  {group.hold_show}
                                </TableCell>
                                <TableCell>
                                  {group.parameters.length} parameters
                                </TableCell>
                                <TableCell>
                                  <IconButton size="small" color="primary">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton size="small" color="error">
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
                                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ margin: 1 }}>
                                      <Typography variant="h6" gutterBottom component="div" sx={{ fontWeight: 600 }}>
                                        Parameters in {group.hold_show}
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ bgcolor: 'grey.100' }}>
                                            <TableCell sx={{ fontWeight: 600 }}>Test Parameter</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Male</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Female</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Children</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Neonates</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>Flags</TableCell>
                                            <TableCell sx={{ fontWeight: 600, width: 100 }}>Actions</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {group.parameters.map((param) => (
                                            <TableRow key={param.trans_code} hover>
                                              <TableCell sx={{ fontWeight: 500 }}>{param.test}</TableCell>
                                              <TableCell>{param.male || '-'}</TableCell>
                                              <TableCell>{param.female || '-'}</TableCell>
                                              <TableCell>{param.children || '-'}</TableCell>
                                              <TableCell>{param.neonates || '-'}</TableCell>
                                              <TableCell>{param.unit || '-'}</TableCell>
                                              <TableCell>
                                                {param.flag ? (
                                                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                    {param.flag.split(',').map((flag, idx) => (
                                                      <Chip
                                                        key={idx}
                                                        label={flag.trim()}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                                      />
                                                    ))}
                                                  </Stack>
                                                ) : '-'}
                                              </TableCell>
                                              <TableCell>
                                                <IconButton 
                                                  size="small" 
                                                  color="primary"
                                                  onClick={() => handleEditParameter(param)}
                                                >
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton 
                                                  size="small" 
                                                  color="error"
                                                  onClick={() => handleDeleteParameter(param)}
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                    </TableBody>
                  </Table>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredLabData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add Lab Parameter Dialog */}
     <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            maxHeight: '90vh'
          }
        }}
      >
  <DialogTitle>
    <Box
      sx={{
        p: 2,
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        color: 'white',
        margin: -3,
        marginBottom: 2,
        borderRadius: '12px 12px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}
      >
        <ScienceIcon sx={{ fontSize: 28 }} />
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Add Lab Parameters
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Define parameters for laboratory tests
        </Typography>
      </Box>
    </Box>
  </DialogTitle>
  
  <DialogContent sx={{ bgcolor: '#f1fcf9', p: 3 }}>
    <Stack spacing={3}>
      {/* Category Selection */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid #c8e6c9',
          background: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 700
            }}
          >
            1
          </Box>
          <Typography variant="h6" fontWeight={700} color="#1b5e20">
            Select Category
          </Typography>
        </Box>
        
        <FormControl fullWidth>
          <InputLabel>Category *</InputLabel>
          <Select
            value={selectedCategory}
            label="Category *"
            onChange={(e) => setSelectedCategory(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#c8e6c9'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#11998e',
                borderWidth: 2
              }
            }}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.cat}>
                {category.cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Parameters Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid #c8e6c9',
          background: 'white'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 700
              }}
            >
              2
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="#1b5e20">
                Parameters
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formData.length} parameter(s) added
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            size="medium"
            startIcon={<AddIcon />}
            onClick={addParameterRow}
            sx={{
              borderRadius: 2,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
                boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
              }
            }}
          >
            Add Parameter
          </Button>
        </Box>

        {/* Parameter Rows */}
        <Stack spacing={2}>
          {formData.map((param, index) => (
            <Card
              key={param.id}
              elevation={0}
              sx={{
                border: '2px solid #e8f5e9',
                borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderColor: '#c8e6c9',
                  boxShadow: '0 4px 12px rgba(17, 153, 142, 0.1)'
                }
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)',
                  p: 1.5,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'white' }}>
                  Parameter {index + 1}
                </Typography>
                {formData.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeParameterRow(param.id)}
                    sx={{
                      color: 'white',
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.3)'
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <CardContent sx={{ p: 3 }}>
                {/* Basic Information */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: '#1b5e20',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#11998e'
                      }}
                    />
                    Basic Information
                  </Typography>
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <TextField
                        fullWidth
                        label="Parameter Name *"
                        value={param.parameterName}
                        onChange={(e) => handleInputChange(param.id, 'parameterName', e.target.value)}
                        placeholder="e.g., Hemoglobin, Glucose, WBC"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#11998e',
                                borderWidth: 2
                              }
                            }
                          },
                          '& .MuiFormLabel-root.Mui-focused': {
                            color: '#11998e'
                          }
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <FormControl fullWidth>
                        <InputLabel>Type</InputLabel>
                        <Select
                          value={param.type}
                          label="Type"
                          onChange={(e) => handleInputChange(param.id, 'type', e.target.value)}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#e0e0e0'
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#11998e',
                              borderWidth: 2
                            }
                          }}
                        >
                          <MenuItem value="independent">Independent</MenuItem>
                          <MenuItem value="dependent">Dependent</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                </Box>

                {/* Reference Ranges */}
                <Box sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: '#1b5e20',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#11998e'
                      }}
                    />
                    Reference Ranges
                  </Typography>
                  <div className="row g-2">
                    <div className="col-12 col-md-6 col-lg-3">
                      <TextField
                        fullWidth
                        label="Male Range"
                        value={param.maleRange}
                        onChange={(e) => handleInputChange(param.id, 'maleRange', e.target.value)}
                        placeholder="e.g., 13.5-17.5"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#11998e',
                                borderWidth: 2
                              }
                            }
                          },
                          '& .MuiFormLabel-root.Mui-focused': {
                            color: '#11998e'
                          }
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <TextField
                        fullWidth
                        label="Female Range"
                        value={param.femaleRange}
                        onChange={(e) => handleInputChange(param.id, 'femaleRange', e.target.value)}
                        placeholder="e.g., 12.0-15.5"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#11998e',
                                borderWidth: 2
                              }
                            }
                          },
                          '& .MuiFormLabel-root.Mui-focused': {
                            color: '#11998e'
                          }
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <TextField
                        fullWidth
                        label="Children Range"
                        value={param.childrenRange}
                        onChange={(e) => handleInputChange(param.id, 'childrenRange', e.target.value)}
                        placeholder="e.g., 11.0-14.0"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#11998e',
                                borderWidth: 2
                              }
                            }
                          },
                          '& .MuiFormLabel-root.Mui-focused': {
                            color: '#11998e'
                          }
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <TextField
                        fullWidth
                        label="Neonate Range"
                        value={param.neonateRange}
                        onChange={(e) => handleInputChange(param.id, 'neonateRange', e.target.value)}
                        placeholder="e.g., 14.0-20.0"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#11998e',
                                borderWidth: 2
                              }
                            }
                          },
                          '& .MuiFormLabel-root.Mui-focused': {
                            color: '#11998e'
                          }
                        }}
                      />
                    </div>
                  </div>
                </Box>

                {/* Additional Information */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      color: '#1b5e20',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: '#11998e'
                      }}
                    />
                    Additional Information
                  </Typography>
                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <TextField
                        fullWidth
                        label="Unit"
                        value={param.unit}
                        onChange={(e) => handleInputChange(param.id, 'unit', e.target.value)}
                        placeholder="e.g., g/dL, mg/dL, mmol/L"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#11998e',
                                borderWidth: 2
                              }
                            }
                          },
                          '& .MuiFormLabel-root.Mui-focused': {
                            color: '#11998e'
                          }
                        }}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <TextField
                        fullWidth
                        label="Flags (comma-separated)"
                        value={param.flags}
                        onChange={(e) => handleInputChange(param.id, 'flags', e.target.value)}
                        placeholder="e.g., normal, high, low, positive, negative"
                        helperText="Enter possible result flags separated by commas"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused': {
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#11998e',
                                borderWidth: 2
                              }
                            }
                          },
                          '& .MuiFormLabel-root.Mui-focused': {
                            color: '#11998e'
                          }
                        }}
                      />
                    </div>
                  </div>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </Paper>
    </Stack>
  </DialogContent>
  
  <DialogActions sx={{ p: 3, bgcolor: '#f1fcf9', gap: 2 }}>
    <Button
      onClick={handleCloseDialog}
      sx={{
        px: 3,
        py: 1,
        borderRadius: 2,
        color: '#666',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
          bgcolor: 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleSave}
      variant="contained"
      disabled={!selectedCategory || formData.every(param => !param.parameterName.trim())}
      sx={{
        px: 4,
        py: 1,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
        '&:hover': {
          background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
          boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
        },
        '&:disabled': {
          background: '#e0e0e0',
          color: '#9e9e9e'
        }
      }}
    >
      💾 Save All Parameters ({formData.filter(param => param.parameterName.trim()).length})
    </Button>
  </DialogActions>
      </Dialog> 

   {/* Add Lab Category Dialog */}
      
          
    <Dialog
      open={openCategoryDialog}
      onClose={handleCloseCategoryDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            color: 'white',
            margin: -3,
            marginBottom: 2,
            borderRadius: '12px 12px 0 0',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <CategoryIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Add Lab Category
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Create a new laboratory test category
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ bgcolor: '#f1fcf9', p: 3 }}>
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid #c8e6c9',
              background: 'white'
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: '#1b5e20',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#11998e'
                }}
              />
              Category Information
            </Typography>
            
            <Stack spacing={2.5}>
              <TextField
                fullWidth
                label="Category Name *"
                value={categoryFormData.categoryName}
                onChange={(e) => handleCategoryInputChange('categoryName', e.target.value)}
                placeholder="e.g., Hematology, Biochemistry, Microbiology"
                helperText="Enter a descriptive name for the new lab category"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#11998e',
                        borderWidth: 2
                      }
                    }
                  },
                  '& .MuiFormLabel-root.Mui-focused': {
                    color: '#11998e'
                  }
                }}
              />

              <TextField
                fullWidth
                type="number"
                label="Waiting Time (Days)"
                value={categoryFormData.waitingTime}
                onChange={(e) => handleCategoryInputChange('waitingTime', e.target.value)}
                placeholder="e.g., 2, 3, 5"
                helperText="Expected turnaround time for test results in days"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AccessTime sx={{ color: '#11998e' }} />
                    </InputAdornment>
                  ),
                  inputProps: { min: 0 }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#11998e',
                        borderWidth: 2
                      }
                    }
                  },
                  '& .MuiFormLabel-root.Mui-focused': {
                    color: '#11998e'
                  }
                }}
              />
            </Stack>
          </Paper>

          {categories.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                border: '1px solid #c8e6c9',
                background: 'white'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1.5,
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="body2" fontWeight={700} color="white">
                    {categories.length}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1b5e20' }}>
                  Existing Categories
                </Typography>
              </Box>
              
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  p: 2,
                  bgcolor: '#f1fcf9',
                  borderRadius: 2,
                  border: '1px solid #e8f5e9',
                  maxHeight: 200,
                  overflowY: 'auto'
                }}
              >
                {categories.map((category) => (
                  <Box
                    key={category.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      p: 1.5,
                      bgcolor: 'white',
                      borderRadius: 1.5,
                      border: '1px solid #c8e6c9',
                      '&:hover': {
                        borderColor: '#11998e',
                        boxShadow: '0 2px 8px rgba(17, 153, 142, 0.1)'
                      }
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} color="#1b5e20">
                      {category.cat}
                    </Typography>
                    <Chip
                      icon={<AccessTime sx={{ fontSize: 14 }} />}
                      label={`${category.waiting_time || 0} days`}
                      size="small"
                      sx={{
                        bgcolor: (category.waiting_time || 0) > 3 ? '#ffebee' : '#e8f5e9',
                        color: (category.waiting_time || 0) > 3 ? '#c62828' : '#2e7d32',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, bgcolor: '#f1fcf9', gap: 2 }}>
        <Button
          onClick={handleCloseCategoryDialog}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            color: '#666',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveCategory}
          variant="contained"
          disabled={!categoryFormData.categoryName.trim()}
          sx={{
            px: 4,
            py: 1,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
              boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
            },
            '&:disabled': {
              background: '#e0e0e0',
              color: '#9e9e9e'
            }
          }}
        >
          💾 Save Category
        </Button>
      </DialogActions>
    </Dialog>

      {/* Edit Lab Category Dialog */}
  <Dialog
  open={editingCategory !== null}
  onClose={handleCloseEditDialog}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 3,
      boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
    }
  }}
>
  <DialogTitle>
    <Box
      sx={{
        p: 2,
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        color: 'white',
        margin: -3,
        marginBottom: 2,
        borderRadius: '12px 12px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: 'rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}
      >
        <CategoryIcon sx={{ fontSize: 28 }} />
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700}>
          Edit Lab Category
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9 }}>
          Update laboratory test category details
        </Typography>
      </Box>
    </Box>
  </DialogTitle>
  
  <DialogContent sx={{ bgcolor: '#f1fcf9', p: 3 }}>
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid #c8e6c9',
          background: 'white'
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: '#1b5e20',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: '#11998e'
            }}
          />
          Category Information
        </Typography>
        
        <Stack spacing={2.5}>
          <TextField
            fullWidth
            label="Category Name *"
            value={editCategoryFormData.categoryName}
            onChange={(e) => setEditCategoryFormData(prev => ({
              ...prev,
              categoryName: e.target.value
            }))}
            placeholder="e.g., Hematology, Biochemistry, Microbiology"
            helperText="Updating this category will also update all lab parameters with the same category name"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#11998e',
                    borderWidth: 2
                  }
                }
              },
              '& .MuiFormLabel-root.Mui-focused': {
                color: '#11998e'
              }
            }}
          />

          <TextField
            fullWidth
            type="number"
            label="Waiting Time (Days)"
            value={editCategoryFormData.waitingTime}
            onChange={(e) => setEditCategoryFormData(prev => ({
              ...prev,
              waitingTime: e.target.value
            }))}
            placeholder="e.g., 2, 3, 5"
            helperText="Expected turnaround time for test results in days"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccessTime sx={{ color: '#11998e' }} />
                </InputAdornment>
              ),
              inputProps: { min: 0 }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#11998e',
                    borderWidth: 2
                  }
                }
              },
              '& .MuiFormLabel-root.Mui-focused': {
                color: '#11998e'
              }
            }}
          />
        </Stack>
      </Paper>

      {/* Warning Box */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: '1px solid #fff3cd',
          background: '#fff9e6'
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <Typography fontSize={16}>⚠️</Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100', mb: 0.5 }}>
              Update Notice
            </Typography>
            <Typography variant="body2" sx={{ color: '#ef6c00', lineHeight: 1.6 }}>
              Changes to this category will affect all associated lab parameters and test results.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Stack>
  </DialogContent>
  
  <DialogActions sx={{ p: 3, bgcolor: '#f1fcf9', gap: 2 }}>
    <Button
      onClick={handleCloseEditDialog}
      sx={{
        px: 3,
        py: 1,
        borderRadius: 2,
        color: '#666',
        fontWeight: 600,
        textTransform: 'none',
        '&:hover': {
          bgcolor: 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleUpdateCategory}
      variant="contained"
      disabled={
        !editCategoryFormData.categoryName.trim() || 
        (editCategoryFormData.categoryName.trim() === editingCategory?.cat &&
         editCategoryFormData.waitingTime === editingCategory?.waiting_time)
      }
      sx={{
        px: 4,
        py: 1,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        fontWeight: 600,
        textTransform: 'none',
        boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)',
        '&:hover': {
          background: 'linear-gradient(135deg, #0e8679 0%, #2ed968 100%)',
          boxShadow: '0 6px 16px rgba(17, 153, 142, 0.4)'
        },
        '&:disabled': {
          background: '#e0e0e0',
          color: '#9e9e9e'
        }
      }}
    >
      ✏️ Update Category
    </Button>
  </DialogActions>
      </Dialog>

      {/* Edit Lab Parameter Dialog */}
      <Dialog
        open={editingParameter !== null}
        onClose={handleCloseEditParameterDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Lab Parameter
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <div className="row">
              <div className="col-12 col-md-6">
                <TextField
                  fullWidth
                  label="Parameter Name *"
                  value={editParameterFormData.parameterName}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    parameterName: e.target.value
                  }))}
                  placeholder="e.g., Hemoglobin, Glucose, WBC"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6">
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={editParameterFormData.type}
                    label="Type"
                    onChange={(e) => setEditParameterFormData(prev => ({
                      ...prev,
                      type: e.target.value
                    }))}
                  >
                    <MenuItem value="independent">Independent</MenuItem>
                    <MenuItem value="dependent">Dependent</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>

            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Reference Ranges
            </Typography>
            <div className="row">
              <div className="col-12 col-md-6 col-lg-3">
                <TextField
                  fullWidth
                  label="Male Range"
                  value={editParameterFormData.maleRange}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    maleRange: e.target.value
                  }))}
                  placeholder="e.g., 13.5-17.5"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <TextField
                  fullWidth
                  label="Female Range"
                  value={editParameterFormData.femaleRange}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    femaleRange: e.target.value
                  }))}
                  placeholder="e.g., 12.0-15.5"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <TextField
                  fullWidth
                  label="Children Range"
                  value={editParameterFormData.childrenRange}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    childrenRange: e.target.value
                  }))}
                  placeholder="e.g., 11.0-14.0"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6 col-lg-3">
                <TextField
                  fullWidth
                  label="Neonate Range"
                  value={editParameterFormData.neonateRange}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    neonateRange: e.target.value
                  }))}
                  placeholder="e.g., 14.0-20.0"
                  sx={{ mb: 2 }}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <TextField
                  fullWidth
                  label="Unit"
                  value={editParameterFormData.unit}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    unit: e.target.value
                  }))}
                  placeholder="e.g., g/dL, mg/dL, mmol/L"
                  sx={{ mb: 2 }}
                />
              </div>
              <div className="col-12 col-md-6">
                <TextField
                  fullWidth
                  label="Flags (comma-separated)"
                  value={editParameterFormData.flags}
                  onChange={(e) => setEditParameterFormData(prev => ({
                    ...prev,
                    flags: e.target.value
                  }))}
                  placeholder="e.g., normal, high, low, positive, negative"
                  helperText="Enter possible result flags separated by commas"
                  sx={{ mb: 2 }}
                />
              </div>
            </div>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEditParameterDialog} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleUpdateParameter}
            variant="contained"
            disabled={!editParameterFormData.parameterName.trim()}
          >
            Update Parameter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
  
};

export default Home;